import React, { useEffect, useMemo, useState } from 'react';
import { Bug, Code2, MessageSquare, RefreshCw, Send } from 'lucide-react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'frage', label: 'Frage', icon: MessageSquare },
];

const shellStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)',
  gap: 16,
  alignItems: 'start',
  minWidth: 0,
};

const panelStyle = {
  background: '#fff',
  border: '1px solid var(--brand-warm-gray)',
  borderRadius: 8,
  overflow: 'hidden',
};

const inputStyle = {
  width: '100%',
  border: '1px solid var(--brand-warm-gray)',
  borderRadius: 6,
  padding: '10px 12px',
  fontSize: 13,
  fontFamily: 'var(--font-body)',
  boxSizing: 'border-box',
};

const FLOW_STATE = {
  done: { label: 'OK', color: 'var(--brand-success)', bg: 'var(--brand-success-bg)' },
  running: { label: 'läuft', color: 'var(--brand-gold-dark)', bg: 'var(--brand-gold-10)' },
  waiting: { label: 'wartet', color: 'var(--brand-text-muted)', bg: 'var(--brand-cream)' },
  error: { label: 'Fehler', color: 'var(--brand-danger)', bg: 'var(--brand-danger-bg)' },
  skipped: { label: 'entfällt', color: 'var(--brand-text-muted)', bg: 'var(--brand-cream)' },
};

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusPill({ children, tone = 'neutral' }) {
  const colors = {
    neutral: { bg: 'var(--brand-cream)', text: 'var(--brand-text-muted)' },
    dry: { bg: 'var(--brand-info-bg)', text: 'var(--brand-info)' },
    live: { bg: 'var(--brand-success-bg)', text: 'var(--brand-success)' },
    running: { bg: 'var(--brand-gold-10)', text: 'var(--brand-gold-dark)' },
    bug: { bg: 'var(--brand-danger-bg)', text: 'var(--brand-danger)' },
    frage: { bg: 'var(--brand-gold-10)', text: 'var(--brand-gold-dark)' },
  }[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', minHeight: 22,
      padding: '2px 8px', borderRadius: 999,
      background: colors.bg, color: colors.text,
      fontSize: 11, fontWeight: 600,
    }}>
      {children}
    </span>
  );
}

export default function DeveloperChatLite() {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingSessionIds, setSendingSessionIds] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    kategorie: 'bug',
    betreff: '',
    nachricht: '',
    affected_system: 'fleet-platform',
    affected_skill: 'developer-chat-lite',
  });

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const loadSessions = async ({ silent = false } = {}) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/developer-chat/sessions`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSessions(await res.json());
      setError('');
    } catch (err) {
      setError('Sessions konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (id) => {
    try {
      const res = await fetch(`${API_URL}/developer-chat/sessions/${id}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const job = data.solver_job || data.solver_result?.payload || {};
      setActiveSession(data);
      setForm(prev => ({
        ...prev,
        kategorie: data.kategorie || prev.kategorie,
        betreff: data.title || prev.betreff,
        affected_system: job.affected_system || prev.affected_system,
        affected_skill: job.affected_skill || prev.affected_skill,
      }));
      setError('');
    } catch {
      setError('Session konnte nicht geladen werden.');
    }
  };

  useEffect(() => {
    loadSessions();
  }, [token]);

  const activeRunning = Boolean(
    activeSession?.is_running ||
    activeSession?.status === 'running' ||
    activeSession?.solver_result?.status === 'running' ||
    activeSession?.messages?.some(message => message.meta?.pending)
  );
  const hasRunningSession = activeRunning || sessions.some(session => session.is_running || session.status === 'running');

  useEffect(() => {
    if (!token || !hasRunningSession) return undefined;
    const timer = setInterval(() => {
      loadSessions({ silent: true });
      if (activeSession?.id) loadSession(activeSession.id);
    }, 2000);
    return () => clearInterval(timer);
  }, [token, hasRunningSession, activeSession?.id]);

  const sendMessage = async () => {
    if (!form.betreff.trim() || !form.nachricht.trim() || activeRunning) return;
    const sendingKey = activeSession?.id ? String(activeSession.id) : 'new';
    setSendingSessionIds(prev => prev.includes(sendingKey) ? prev : [...prev, sendingKey]);
    try {
      const res = await fetch(`${API_URL}/developer-chat/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...form,
          session_id: activeSession?.id || null,
          priority: 'normal',
          create_feedback_ticket: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setActiveSession(data);
      setSessions(prev => {
        const rest = prev.filter(s => s.id !== data.id);
        return [data, ...rest];
      });
      setForm(prev => ({ ...prev, betreff: data.title || prev.betreff, nachricht: '' }));
      setError('');
    } catch (err) {
      setError(err.message || 'Nachricht konnte nicht gespeichert werden.');
    } finally {
      setSendingSessionIds(prev => prev.filter(id => id !== sendingKey));
    }
  };

  const startNew = () => {
    setActiveSession(null);
    setForm({
      kategorie: 'bug',
      betreff: '',
      nachricht: '',
      affected_system: 'fleet-platform',
      affected_skill: 'developer-chat-lite',
    });
    setError('');
  };

  const result = activeSession?.solver_result || {};
  const payload = result.payload || activeSession?.solver_job || {};
  const fixFlow = result.fix_flow || [];
  const sendingKey = activeSession?.id ? String(activeSession.id) : 'new';
  const sending = sendingSessionIds.includes(sendingKey);
  const canSend = !sending && !activeRunning && form.betreff.trim() && form.nachricht.trim();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--font-headline)',
            fontSize: 24,
            fontWeight: 400,
            color: 'var(--brand-navy)',
            margin: 0,
          }}>
            Developer Chat Lite
          </h1>
          <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
            {activeRunning ? (
              <StatusPill tone="running">läuft</StatusPill>
            ) : activeSession?.solver_result?.live_call ? (
              <StatusPill tone="live">Live-Solver</StatusPill>
            ) : (
              <StatusPill tone="dry">Dry-Run</StatusPill>
            )}
            <StatusPill>intern</StatusPill>
          </div>
        </div>
        <button
          onClick={startNew}
          style={{
            border: 'none', borderRadius: 6,
            background: 'var(--brand-gold)', color: 'var(--brand-navy)',
            padding: '9px 14px', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >
          Neue Session
        </button>
      </div>

      {error && (
        <div style={{
          marginBottom: 12, padding: '10px 12px',
          background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)',
          borderRadius: 6, fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <div style={shellStyle}>
        <aside style={panelStyle}>
          <div style={{
            padding: '12px 14px', borderBottom: '1px solid var(--brand-warm-gray)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <strong style={{ fontSize: 13, color: 'var(--brand-navy)' }}>Sessions</strong>
            <button
              onClick={loadSessions}
              title="Aktualisieren"
              style={{
                width: 30, height: 30, borderRadius: 6,
                border: '1px solid var(--brand-warm-gray)',
                background: '#fff', color: 'var(--brand-text-muted)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <RefreshCw size={15} />
            </button>
          </div>
          <div style={{ maxHeight: 560, overflowY: 'auto' }}>
            {loading && <div style={{ padding: 16, fontSize: 13, color: 'var(--brand-text-muted)' }}>Lädt...</div>}
            {!loading && sessions.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--brand-text-muted)' }}>Keine Sessions</div>
            )}
            {sessions.map(session => {
              const isActive = activeSession?.id === session.id;
              const isRunning = session.is_running || session.status === 'running';
              const isResolved = session.status === 'resolved';
              return (
                <button
                  key={session.id}
                  onClick={() => loadSession(session.id)}
                  style={{
                    width: '100%', textAlign: 'left', border: 'none',
                    borderLeft: isResolved ? '4px solid var(--brand-success)' : '4px solid transparent',
                    borderBottom: '1px solid var(--brand-warm-gray)',
                    background: isResolved ? 'var(--brand-success-bg)' : isActive ? 'var(--brand-cream)' : '#fff',
                    padding: '12px 14px 12px 10px', cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  <div style={{
                    fontSize: 13, color: 'var(--brand-navy)', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {session.title}
                  </div>
                  <div style={{ marginTop: 7, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <StatusPill tone={session.kategorie === 'bug' ? 'bug' : 'frage'}>
                      {session.kategorie === 'bug' ? 'Bug' : 'Frage'}
                    </StatusPill>
                    {isRunning && (
                      <StatusPill tone="running">läuft</StatusPill>
                    )}
                    {isResolved && !isRunning && (
                      <StatusPill tone="live">gelöst</StatusPill>
                    )}
                    <span style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>
                      {formatDate(session.updated_at)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <main style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, minWidth: 0 }}>
          <section style={panelStyle}>
            <div style={{ padding: 16, borderBottom: '1px solid var(--brand-warm-gray)' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {CATEGORY_OPTIONS.map(option => {
                  const Icon = option.icon;
                  const active = form.kategorie === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setForm(prev => ({ ...prev, kategorie: option.value }))}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        minHeight: 36, padding: '0 12px', borderRadius: 6,
                        border: active ? '1px solid var(--brand-gold)' : '1px solid var(--brand-warm-gray)',
                        background: active ? 'var(--brand-gold-10)' : '#fff',
                        color: active ? 'var(--brand-navy)' : 'var(--brand-text-muted)',
                        cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        fontFamily: 'var(--font-body)',
                      }}
                    >
                      <Icon size={15} />
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
                <input
                  value={form.betreff}
                  onChange={e => setForm(prev => ({ ...prev, betreff: e.target.value }))}
                  placeholder="Betreff"
                  style={inputStyle}
                />
                <input
                  value={form.affected_skill}
                  onChange={e => setForm(prev => ({ ...prev, affected_skill: e.target.value }))}
                  placeholder="Skill"
                  style={inputStyle}
                />
              </div>

              <textarea
                value={form.nachricht}
                onChange={e => setForm(prev => ({ ...prev, nachricht: e.target.value }))}
                placeholder="Bug oder Frage beschreiben"
                rows={6}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                <input
                  value={form.affected_system}
                  onChange={e => setForm(prev => ({ ...prev, affected_system: e.target.value }))}
                  placeholder="System"
                  style={{ ...inputStyle, flex: '1 1 180px', maxWidth: 260 }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!canSend}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    minHeight: 38, padding: '0 16px', border: 'none', borderRadius: 6,
                    background: !canSend ? 'var(--brand-warm-gray)' : 'var(--brand-navy)',
                    color: !canSend ? 'var(--brand-text-muted)' : '#fff',
                    cursor: !canSend ? 'default' : 'pointer', fontSize: 13, fontWeight: 700,
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  <Send size={15} />
                  {activeRunning ? 'Wird geprüft' : 'Nachricht senden'}
                </button>
              </div>
            </div>

            <div style={{ padding: 16, minHeight: 280 }}>
              {!activeSession && (
                <div style={{ color: 'var(--brand-text-muted)', fontSize: 13 }}>
                  Noch keine Session ausgewählt.
                </div>
              )}
              {activeSession?.messages?.map(message => (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: 12,
                  }}
                >
                  <div style={{
                    maxWidth: '82%',
                    borderRadius: 8,
                    padding: '10px 12px',
                    background: message.role === 'user' ? 'var(--brand-navy)' : 'var(--brand-cream)',
                    color: message.role === 'user' ? '#fff' : 'var(--brand-text)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    opacity: message.meta?.pending ? 0.78 : 1,
                  }}>
                    {message.content}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside style={panelStyle}>
            <div style={{
              padding: '12px 14px', borderBottom: '1px solid var(--brand-warm-gray)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Code2 size={16} color="var(--brand-gold)" />
              <strong style={{ fontSize: 13, color: 'var(--brand-navy)' }}>Solver-Run</strong>
            </div>
            <div style={{ padding: 14 }}>
              {activeSession ? (
                <>
                  <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Status</span>
                      <StatusPill tone={activeRunning ? 'running' : result.live_call ? 'live' : 'dry'}>
                        {result.status || activeSession.status}
                      </StatusPill>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>FeedbackTicket</span>
                      <strong style={{ fontSize: 12, color: 'var(--brand-navy)' }}>
                        {activeSession.feedback_ticket_id ? `#${activeSession.feedback_ticket_id}` : '-'}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Live-Ausführung</span>
                      <strong style={{ fontSize: 12, color: result.live_call ? 'var(--brand-success)' : 'var(--brand-danger)' }}>
                        {result.live_call ? 'aktiv' : 'deaktiviert'}
                      </strong>
                    </div>
                  </div>
                  {fixFlow.length > 0 && (
                    <div style={{
                      marginBottom: 14,
                      border: '1px solid var(--brand-warm-gray)',
                      borderRadius: 6,
                      overflow: 'hidden',
                    }}>
                      {fixFlow.map(step => {
                        const state = FLOW_STATE[step.state] || FLOW_STATE.waiting;
                        return (
                          <div
                            key={step.key}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '72px minmax(0, 1fr)',
                              gap: 10,
                              padding: '9px 10px',
                              borderBottom: step.key === fixFlow[fixFlow.length - 1].key ? 'none' : '1px solid var(--brand-warm-gray)',
                              background: '#fff',
                            }}
                          >
                            <span style={{
                              alignSelf: 'start',
                              justifySelf: 'start',
                              padding: '2px 7px',
                              borderRadius: 999,
                              background: state.bg,
                              color: state.color,
                              fontSize: 10,
                              fontWeight: 700,
                            }}>
                              {state.label}
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-navy)' }}>
                                {step.label}
                              </div>
                              {step.detail && (
                                <div style={{ marginTop: 2, fontSize: 11, lineHeight: 1.4, color: 'var(--brand-text-muted)', overflowWrap: 'anywhere' }}>
                                  {step.detail}
                                </div>
                              )}
                              {step.url && (
                                <a
                                  href={step.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ display: 'inline-block', marginTop: 4, fontSize: 11, color: 'var(--brand-info)' }}
                                >
                                  Details öffnen
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <pre style={{
                    margin: 0,
                    padding: 12,
                    borderRadius: 6,
                    background: '#101827',
                    color: '#dbeafe',
                    fontSize: 11,
                    lineHeight: 1.45,
                    overflow: 'auto',
                    maxHeight: 420,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {JSON.stringify(payload, null, 2)}
                  </pre>
                </>
              ) : (
                <div style={{ color: 'var(--brand-text-muted)', fontSize: 13 }}>
                  Kein Solver-Run vorbereitet.
                </div>
              )}
            </div>
          </aside>
        </main>
      </div>
    </div>
  );
}
